import { Component, DestroyRef, effect, inject, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
    MatTableDataSource,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
} from '@angular/material/table';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { filter, forkJoin, switchMap } from 'rxjs';
import { RestrictionTypes } from 'src/app/models/centrex/enums/restriction-types';
import { Restriction } from 'src/app/models/centrex/restriction';
import { RestrictionService } from 'src/app/services/centrex/restriction.service';
import { DialogDeleteConfirmationComponent } from '../../dialogs/dialog-delete-confirmation/dialog-delete-confirmation.component';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { ExtensionDisplayPipe } from 'src/app/pipes/extension-display.pipe';
import { CalendarService } from 'src/app/services/centrex/calendar.service';
import { ExtensionDisplayPipe as ExtensionDisplayPipe_1 } from '../../../pipes/extension-display.pipe';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-restrictions',
    templateUrl: './restrictions.component.html',
    styleUrls: ['./restrictions.component.scss'],
    imports: [
        FaIconComponent,
        MatButton,
        RouterLink,
        MatIcon,
        ReactiveFormsModule,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatSortHeader,
        MatFormField,
        MatInput,
        MatIconButton,
        MatSuffix,
        MatTooltip,
        MatCellDef,
        MatCell,
        MatCheckbox,
        MatSelect,
        MatOption,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        TranslateModule,
        ExtensionDisplayPipe_1,
    ]
})
export class RestrictionsComponent {
    private readonly instanceService = inject(InstanceService);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    displayedColumns: string[] = ['name', 'enabled', 'extension', 'type', 'calendar', 'actions'];
    dataSource: MatTableDataSource<Restriction>;
    deleteConfirmationDialog: MatDialogRef<DialogDeleteConfirmationComponent>;

    restrictionTypes = Object.keys(RestrictionTypes);

    filtersForm: UntypedFormGroup;

    checkBoxStatus: boolean = null;

    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;

    constructor(
        private restrictionService: RestrictionService,
        private extensionService: ExtensionService,
        private calendarService: CalendarService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService,
        private extensionDisplayPipe: ExtensionDisplayPipe
    ) {
        this.filtersForm = new UntypedFormGroup({});

        this.initFiltersForm();

        effect(() => {
            if (this.instanceService.instance()) {
                this.loadDatas();
                this.initFiltersForm();
            }
        });
    }

    /**
     * Initialisation du formulaire des filtres du tableau des restrictions
     */
    initFiltersForm() {
        this.filtersForm = this.formbuilder.group({
            id: [''],
            name: [''],
            type: [''],
            enabled: [this.checkBoxStatus],
            extension: [''],
            calendar: [''],
        });
    }

    loadDatas() {
        this.spinner.show();

        forkJoin([this.restrictionService.getRestrictions(), this.extensionService.getExtensions(), this.calendarService.getCalendars()])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ([restrictions, extensions, calendars]) => {
                    restrictions.forEach((restriction) => {
                        restriction['extension'] = extensions.find((extension) => extension.id === restriction.extensionId);
                        restriction['calendar'] = restriction.calendarId ? calendars.find((calendar) => calendar.id === restriction.calendarId).name : '';
                    });

                    this.dataSource = new MatTableDataSource(restrictions);

                    this.dataSource.paginator = this.paginator;
                    this.dataSource.sort = this.sort;

                    this.dataSource.sortingDataAccessor = (item, property) => {
                        if (property === 'extension') {
                            return this.extensionDisplayPipe.transform(item['extension']);
                        } else {
                            return item[property];
                        }
                    };

                    this.createFilters();

                    this.spinner.hide();
                },
                error: () => {
                    this.spinner.hide();
                },
            });
    }

    createFilters() {
        this.dataSource.filterPredicate = (data: Restriction, filter: string) => {
            const filterArray = filter.split('¤');
            const matchFilter = [];

            matchFilter.push(data.id && data.id.trim().toLowerCase().includes(filterArray[0].trim().toLowerCase()));

            matchFilter.push(data.name && data.name.trim().toLowerCase().includes(filterArray[1].trim().toLowerCase()));

            if (filterArray[2] !== '') {
                matchFilter.push(data.enabled === Boolean(JSON.parse(filterArray[2])));
            }

            if (filterArray[3] !== '') {
                matchFilter.push(data['extension'] && this.extensionDisplayPipe.transform(data['extension']).trim().toLowerCase().includes(filterArray[3].trim().toLowerCase()));
            }

            if (filterArray[4]) {
                matchFilter.push(
                    data.type &&
                        this.translate.instant('EnumRestrictionTypes.' + data.type.trim().toLowerCase()).includes(this.translate.instant('EnumRestrictionTypes.' + filterArray[4]))
                );
            }

            if (filterArray[5] !== '') {
                matchFilter.push(data['calendar'] && data['calendar'].trim().toLowerCase().includes(filterArray[5].trim().toLowerCase()));
            }

            return matchFilter.every(Boolean);
        };

        this.displayedColumns = ['name', 'enabled', 'extension', 'type', 'calendar', 'actions'];

        this.applyFilters();
    }

    /**
     * Application du filtre dans le tableau des restrictions
     */
    applyFilters() {
        const id = this.filtersForm.get('id').value;
        const name = this.filtersForm.get('name').value;
        const enabled = this.filtersForm.get('enabled').value;
        const extension = this.filtersForm.get('extension').value;
        const type = this.filtersForm.get('type').value;
        const calendar = this.filtersForm.get('calendar').value;

        const filterValue =
            (!!id ? id : '') +
            '¤' +
            (!!name ? name : '') +
            '¤' +
            (enabled !== null ? enabled : '') +
            '¤' +
            (!!extension ? extension : '') +
            '¤' +
            (!!type ? type : '') +
            '¤' +
            (!!calendar ? calendar : '');

        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    /**
     * Réinitialise le filtre de la colonne spécifiée.
     * @param formControlName Nom du contrôle du formulaire
     */
    resetColumnFilter(formControlName: string): void {
        if (formControlName === 'enabled') {
            this.checkBoxStatus = null;
        }

        this.filtersForm.controls[formControlName].reset();
        this.applyFilters();
    }

    /**
     * Réinitialise tous les filtres de colonne
     */
    resetAllColumnFilter() {
        this.checkBoxStatus = null;
        this.filtersForm.reset();
        this.applyFilters();
    }

    onDeleteClick(restrictionId: string, restrictionName: string) {
        this.deleteConfirmationDialog = this.dialog.open(DialogDeleteConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });
        this.deleteConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogDeleteConfirmation.RestrictionConfirmMessage', {
            restrictionName: restrictionName,
        });

        this.deleteConfirmationDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => this.restrictionService.deleteRestriction(restrictionId)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.toastr.success(this.translate.instant('Restrictions.SuccessMessageDeleteRestriction', { restrictionName: restrictionName }));
                    this.loadDatas();
                },
                error: () => {
                    this.toastr.error(this.translate.instant('Restrictions.ErrorMessageDeleteRestriction', { restrictionName: restrictionName }));
                },
                complete: () => {
                    this.deleteConfirmationDialog = null;
                },
            });
    }

    checkBoxChange() {
        switch (this.checkBoxStatus) {
            case false:
                this.checkBoxStatus = null;
                this.filtersForm.get('enabled').setValue(null);
                break;

            case null:
                this.checkBoxStatus = true;
                break;

            case true:
                this.checkBoxStatus = false;
                break;
        }

        this.applyFilters();
    }
}
